import type { FC, ReactNode } from 'react'

import { Seo } from '@/components/generals/seo'
import { Layout } from '@/components/generals/layout'
import { Reveal } from '@/components/generals/reveal'
import { BigLink } from '@/components/generals/big-link'
import { RuleDraw } from '@/components/generals/rule-draw'
import { ImagePlate } from '@/components/generals/image-plate'
import { WordReveal } from '@/components/generals/word-reveal'
import { AboutHero } from '@/components/about/about-hero'
import { Colophon } from '@/components/about/colophon'
import { IndexRail } from '@/components/about/index-rail'
import { MethodPanels } from '@/components/about/method-panels'
import { PracticeLedger } from '@/components/about/practice-ledger'
import { useParallax } from '@/hooks/use-parallax'

import { MICRO, MICRO_FAINT } from '@/constants/type'

const SectionMark: FC<{ index: string; label: string }> = ({ index, label }): ReactNode => (
    <div className='col-span-12 flex items-center gap-4 sm:col-span-2'>
        <span className={MICRO_FAINT}>{index}</span>

        <span aria-hidden='true' className='block h-px w-4 bg-fz-rule sm:hidden' />

        <span className={`${MICRO} text-fz-ink`}>{label}</span>
    </div>
)

const About: FC = (): ReactNode => {
    const noteDrift = useParallax<HTMLDivElement>(-46)

    return (
        <Layout>
            <Seo
                title='About'
                description='Frieze is a design studio of nine, working between London and Lisbon since 2016 across identity, typeface design, editorial and wayfinding.'
            />

            <article>
                <AboutHero />

                <div className='px-6 sm:px-12'>
                    <div className='flex gap-12'>
                        <aside className='hidden w-36 shrink-0 lg:block'>
                            <IndexRail />
                        </aside>

                        <div className='min-w-0 flex-1'>
                            {/* 01 — Position */}
                            <section id='position' className='scroll-mt-28 pt-24 sm:pt-36'>
                                <Reveal>
                                    <div className='grid grid-cols-12 gap-x-6'>
                                        <SectionMark index='01' label='Position' />
                                    </div>
                                </Reveal>

                                <div className='mt-10 grid grid-cols-12 gap-x-6 gap-y-12 sm:mt-14'>
                                    <div className='col-span-12 sm:col-start-3 sm:col-span-7'>
                                        <WordReveal
                                            as='p'
                                            trigger='scroll'
                                            stagger={0.045}
                                            text='Most work is made to be scanned. We are interested in the other kind.'
                                            className='max-w-[38ch] font-serif tracking-[-0.015em] text-fz-ink'
                                            style={{
                                                fontSize: 'clamp(1.375rem, 2.6vw, 2.125rem)',
                                                lineHeight: 1.24
                                            }}
                                        />

                                        <Reveal delay={0.15}>
                                            <p className='mt-10 max-w-[58ch] text-[15px] leading-relaxed text-fz-sub'>
                                                The sign you read twice because the arrow sits at
                                                exactly the right weight. The book whose margin
                                                gives the photograph room to breathe. The label you
                                                keep after the bottle is empty. None of that happens
                                                by accident, and none of it survives a process built
                                                for speed.
                                            </p>
                                        </Reveal>

                                        <Reveal delay={0.22}>
                                            <p className='mt-5 max-w-[58ch] text-[15px] leading-relaxed text-fz-sub'>
                                                So we work slowly and we work close. A project
                                                usually starts with a week of reading and ends with
                                                a week of proofing. The part in between is mostly
                                                subtraction.
                                            </p>
                                        </Reveal>
                                    </div>

                                    <aside className='col-span-12 sm:col-start-11 sm:col-span-2'>
                                        <div ref={noteDrift}>
                                            <RuleDraw />

                                            <Reveal delay={0.1}>
                                                <div className='pt-4'>
                                                    <p className={MICRO_FAINT}>Note</p>

                                                    <p className='mt-3 text-[13px] leading-relaxed text-fz-sub'>
                                                        Six projects a year. Two of them are usually
                                                        type.
                                                    </p>
                                                </div>
                                            </Reveal>
                                        </div>
                                    </aside>
                                </div>
                            </section>

                            {/* Full-measure spread */}
                            <div className='pt-24 sm:pt-36'>
                                <ImagePlate
                                    src='/images/about-spread.jpg'
                                    alt='Portrait cut by the light through a blind'
                                    ratio='16 / 9'
                                    focus='50% 50%'
                                    drift={-52}
                                    caption='Halcyon — light study, sheet 11'
                                />
                            </div>

                            {/* Pull quote */}
                            <div className='pt-24 sm:pt-36'>
                                <blockquote className='grid grid-cols-12 gap-x-6'>
                                    <WordReveal
                                        as='p'
                                        trigger='scroll'
                                        stagger={0.05}
                                        text='Restraint is not an absence of ideas. It is the last idea, arrived at late.'
                                        className='col-span-12 font-serif tracking-[-0.02em] text-fz-ink sm:col-start-2 sm:col-span-10'
                                        style={{
                                            fontSize: 'clamp(1.75rem, 4.4vw, 3.5rem)',
                                            lineHeight: 1.1
                                        }}
                                    />

                                    <Reveal
                                        delay={0.5}
                                        className='col-span-12 mt-8 sm:col-start-2 sm:col-span-10'
                                    >
                                        <footer className={MICRO_FAINT}>
                                            Nadia Okonkwo — Founding partner
                                        </footer>
                                    </Reveal>
                                </blockquote>
                            </div>

                            {/* 02 — Practice */}
                            <section id='practice' className='scroll-mt-28 pt-24 sm:pt-40'>
                                <Reveal>
                                    <div className='grid grid-cols-12 gap-x-6 gap-y-6'>
                                        <SectionMark index='02' label='Practice' />

                                        <p className='col-span-12 max-w-[48ch] text-[15px] leading-relaxed text-fz-sub sm:col-start-3 sm:col-span-6'>
                                            Six disciplines, one studio. Most projects touch three
                                            of them before they are finished.
                                        </p>
                                    </div>
                                </Reveal>

                                <PracticeLedger />
                            </section>

                            {/* 03 — Method */}
                            <section id='method' className='scroll-mt-28 pt-24 sm:pt-40'>
                                <Reveal>
                                    <div className='grid grid-cols-12 gap-x-6 gap-y-6'>
                                        <SectionMark index='03' label='Method' />

                                        <p className='col-span-12 max-w-[48ch] text-[15px] leading-relaxed text-fz-sub sm:col-start-3 sm:col-span-6'>
                                            Three movements, in order, every time. The order is the
                                            method.
                                        </p>
                                    </div>
                                </Reveal>

                                <MethodPanels />
                            </section>

                            {/* 04 — Colophon */}
                            <section id='colophon' className='scroll-mt-28 pt-24 sm:pt-40'>
                                <Reveal>
                                    <div className='grid grid-cols-12 gap-x-6'>
                                        <SectionMark index='04' label='Colophon' />
                                    </div>
                                </Reveal>

                                <div className='mt-10 grid grid-cols-12 gap-x-6 gap-y-12 sm:mt-14'>
                                    <div className='col-span-12 lg:col-span-8'>
                                        <Colophon />
                                    </div>

                                    <ImagePlate
                                        className='col-span-8 sm:col-span-5 lg:col-start-10 lg:col-span-3'
                                        src='/images/about-colophon.jpg'
                                        alt='Portrait under a hand-cast shadow'
                                        ratio='3 / 4'
                                        focus='50% 42%'
                                        drift={-30}
                                        caption='Studio, London'
                                    />
                                </div>
                            </section>

                            {/* Closing */}
                            <section id='closing' className='pt-24 sm:pt-40'>
                                <RuleDraw />

                                <Reveal>
                                    <div className='pt-8'>
                                        <BigLink
                                            href='/contact'
                                            eyebrow='Next'
                                            label='Start a conversation'
                                        />
                                    </div>
                                </Reveal>
                            </section>
                        </div>
                    </div>
                </div>
            </article>
        </Layout>
    )
}

export default About
